import React, { memo, useCallback, useEffect, useRef } from 'react';
import type { EditorStarterItem } from '../../../items/item-type';
import {
  useCurrentStateAsRef,
  useWriteContext,
} from '../../../utils/use-context';
import { timelineScrollContainerRef } from '../../../utils/restore-scroll-after-zoom';
import { TimelineItemExtendHandle } from '../timeline-item-extend-handle';
import { startPointerTrim } from '@waka/compat/pointer-trim';

interface TimelineItemExtendHandles {
  item: EditorStarterItem;
  timelineWidth: number;
  width: number;
  height: number;
  isLocked?: boolean;
}
export const TimelineItemExtendHandles = memo(
  ({
    item,
    timelineWidth,
    width,
    height,
    isLocked = false,
  }: TimelineItemExtendHandles) => {
    const { setState } = useWriteContext();
    const stateAsRef = useCurrentStateAsRef();
    const active = useRef<(() => void) | null>(null);
    useEffect(
      () => () => {
        if (!timelineScrollContainerRef.current?.isConnected)
          active.current?.();
        active.current = null;
      },
      [],
    );
    const begin = useCallback(
      (event: React.PointerEvent<HTMLDivElement>, side: 'left' | 'right') => {
        if (event.button !== 0 || isLocked || active.current) return;
        active.current = startPointerTrim({
          event,
          stateRef: stateAsRef,
          setState,
          itemId: item.id,
          side,
          timelineWidth,
          height,
          onEnd: () => {
            active.current = null;
          },
        });
      },
      [isLocked, stateAsRef, setState, item.id, timelineWidth, height],
    );
    if (isLocked) return null;
    const hitWidth = Math.max(1, Math.min(width / 2, 8));
    return (
      <>
        <TimelineItemExtendHandle
          className="cursor-ew-resize"
          aria-label="Trim clip start"
          title="클립 시작 조절"
          onPointerDown={(event) => begin(event, 'left')}
          style={{ width: hitWidth, left: -1, touchAction: 'none' }}
        />
        <TimelineItemExtendHandle
          className="cursor-ew-resize"
          aria-label="Trim clip end"
          title="클립 끝 조절"
          onPointerDown={(event) => begin(event, 'right')}
          style={{ width: hitWidth, right: -1, touchAction: 'none' }}
        />
      </>
    );
  },
);
TimelineItemExtendHandles.displayName = 'TimelineItemExtendHandles';
